/* eslint-disable @typescript-eslint/no-explicit-any */
import { HttpClient, HttpParams } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { Observable } from 'rxjs'
import { AccountSummary } from '../models/account-summary'
import { CorporateAction } from '../models/corporate-action'
import { CorporateActionInput } from '../models/corporate-action-input'
import { CorporateActionInputEdit } from '../models/corporate-action-input-edit'
import { CorporateActionType } from '../models/corporate-action-type'
import { SearchParams } from '../models/search-params'
import { SearchRequest, SecurityResponse } from '../models/security-model'

const CORPORATE_ACTIONS_HOST = 'helios-corporate-actions'
const CORPORATE_ACTIONS_SEARCH_URL = `${CORPORATE_ACTIONS_HOST}/search`
const CORPORATE_ACTIONS_SEARCH_ISSUE_URL = `${CORPORATE_ACTIONS_HOST}/search/issue`
const CORPORATE_ACTIONS_TYPE_URL = `${CORPORATE_ACTIONS_HOST}/model/type`
const CORPORATE_ACTIONS_ISSUE_URL = `${CORPORATE_ACTIONS_HOST}/jira/issue`
const CORPORATE_ACTIONS_MANAGE_URL = `${CORPORATE_ACTIONS_HOST}/manage/`
const CORPORATE_ACTIONS_USER_POLICY = `${CORPORATE_ACTIONS_HOST}/actions`
/*@internal*/
export const CORPORATE_ACTIONS_BULK_SECURITY = `${CORPORATE_ACTIONS_HOST}/search/bulk`

@Injectable({
  providedIn: 'root',
})
export class CorporateActionsService {
  constructor(private readonly httpClient: HttpClient) {}

  searchByParams(searchParams: SearchParams): Observable<CorporateAction[]> {
    return this.httpClient.post<CorporateAction[]>(
      CORPORATE_ACTIONS_SEARCH_URL,
      searchParams,
    )
  }

  searchByIssue(issueId: string): Observable<CorporateAction[]> {
    return this.httpClient.get<CorporateAction[]>(
      `${CORPORATE_ACTIONS_SEARCH_ISSUE_URL}?issueId=${issueId}`,
    )
  }

  searchById(
    id: string,
    accountIds: number[],
    issueId?: string,
  ): Observable<CorporateAction> {
    let params = new HttpParams()
    if (accountIds) {
      for (const accountId of accountIds) {
        params = params.append('accountId', String(accountId))
      }
    }
    if (issueId) {
      params = params.append('issueId', issueId)
    }

    const options = {
      params,
    }
    return this.httpClient.get<CorporateAction>(
      `${CORPORATE_ACTIONS_SEARCH_URL}/${id}`,
      options,
    )
  }

  searchAccountsAffected(id: string): Observable<AccountSummary> {
    return this.httpClient.get<AccountSummary>(
      `${CORPORATE_ACTIONS_SEARCH_URL}/${id}/accounts`,
    )
  }

  getCorporateActionTypes(): Observable<CorporateActionType[]> {
    return this.httpClient.get<CorporateActionType[]>(
      `${CORPORATE_ACTIONS_TYPE_URL}`,
    )
  }

  verifyIssue(key: string): Observable<boolean> {
    return this.httpClient.get<boolean>(`${CORPORATE_ACTIONS_ISSUE_URL}/${key}`)
  }

  addCorporateAction(input: CorporateActionInput): Observable<string> {
    return this.httpClient.post<string>(CORPORATE_ACTIONS_MANAGE_URL, input)
  }

  editCorporateAction(
    input: CorporateActionInputEdit,
    id: string,
  ): Observable<unknown> {
    return this.httpClient.put(CORPORATE_ACTIONS_MANAGE_URL + `${id}`, input)
  }

  deleteCorporateAction(id: string): Observable<unknown> {
    return this.httpClient.delete(CORPORATE_ACTIONS_MANAGE_URL + `${id}`)
  }

  getSecurityCandidates(cusips: string[]): Observable<SecurityResponse[]> {
    const body = cusips.map(cusip => <SearchRequest>{ term: cusip })
    return this.httpClient.post<SecurityResponse[]>(
      CORPORATE_ACTIONS_BULK_SECURITY,
      body,
    )
  }

  corporateActionUserPolicy(): Observable<string[]> {
    return this.httpClient.get<string[]>(`${CORPORATE_ACTIONS_USER_POLICY}`)
  }
}
